import { FC, useState, ReactNode, MouseEvent } from 'react';
import MuiMenu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Box, ListItemIcon, PopoverOrigin } from '@mui/material';
import Icon from '../icon';
import { createUseStyles } from 'react-jss';
import MenuHeader from './header';
import Text from '../text';
import { IconType } from '../icon/helpers';
import { MenuProvider } from '@/contexts/menu';

export type MenuItemConfig = {
  id: string;
  label: ReactNode;
  onClick: () => void;
  icon?: IconType;
};

interface Props {
  trigger: ReactNode;
  menuId?: string;
  menuItems?: MenuItemConfig[];
  children?: ReactNode;
  className?: string;
  title?: string;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  onClose?: () => void;
  onOpen?: () => void;
  disabled?: boolean;
}

const useStyles = createUseStyles({
  trigger: {
    cursor: 'pointer',
    display: 'block',
  },
  menu: {
    padding: '2px 10px',
    minWidth: 300,
  },
});

const Menu: FC<Props> = ({
  trigger,
  menuId,
  menuItems = [],
  children,
  className = '',
  title,
  transformOrigin = {
    vertical: 'top',
    horizontal: 'right',
  },
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
  },
  onClose,
  onOpen,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = !!anchorEl;
  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }
    setAnchorEl(event.currentTarget);
    onOpen?.();
  };
  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };
  const styles = useStyles();

  let MenuComp = children;

  if (menuItems.length) {
    MenuComp = menuItems.map(({ id, label, onClick, icon }) => {
      const hasIcon = !!icon;
      return (
        <MenuItem key={id} onClick={onClick}>
          {hasIcon && (
            <ListItemIcon>
              <Icon name={icon as IconType} size="small" />
            </ListItemIcon>
          )}
          <Text size="small"> {label}</Text>
        </MenuItem>
      );
    });
  }

  const hasTitle = Boolean(title);

  return (
    <Box className={className}>
      <span
        className={styles.trigger}
        id={menuId}
        role="button"
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
      >
        {trigger}
      </span>
      <MuiMenu
        id={menuId}
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': menuId,
        }}
        slotProps={{
          paper: { className: styles.menu, elevation: 1 },
        }}
      >
        <MenuProvider value={handleClose}>
          {hasTitle && <MenuHeader title={title!} onClose={handleClose} />}
          {MenuComp}
        </MenuProvider>
      </MuiMenu>
    </Box>
  );
};

export default Menu;
